import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'

// ---------------------------------------------------------------------------
// Reference / lookup tables
// ---------------------------------------------------------------------------

// Maps `documents.types` codes to display labels. Extend as new types appear.
const DOCUMENT_TYPE_LABELS = {
  map: 'Map',
  audio: 'Audio material / podcast'
}

// Maps `projects.macro_regional_strategies` codes to display labels.
const MACRO_REGIONAL_STRATEGY_LABELS = {
  eusbsr: 'EUSBSR',
  alpine: 'EUSALP',
  danube: 'EUSDR',
  adriatic_ionian: 'EUSAIR'
}

// Baseline "full" year range. A start/end range is only surfaced as a filter
// if it differs from this default (i.e. the user actually narrowed it down).
const DEFAULT_YEAR_RANGE = [2000, 2025]

// ---------------------------------------------------------------------------
// Helpers for reading the `currentFilters` UI-config structure
// ---------------------------------------------------------------------------
//
// `currentFilters` is an array of sections, each with a `filters` array.
// Each filter entry has a `values` array (the key(s) it corresponds to in
// `selectedFilters`) and a `data` field whose shape varies:
//   - array of [id, label] tuples            e.g. fields
//   - plain object map { id: label }         e.g. languages
//   - array of strings                       e.g. partner status
//   - array of { id, content }                e.g. specific objectives
//   - array of { id, title }                  e.g. programme periods
//   - array of { name, title }                e.g. document types
//
// The helpers below normalize any of these into a flat [{ id, label }] list
// so the full set of options (and their labels) can always be read directly
// from `currentFilters`, rather than duplicated in a hardcoded table.

/** Finds the filter definition whose `values` array includes `valueKey`. */
const findFilterDef = (currentFilters, valueKey) => {
  if (!Array.isArray(currentFilters)) return null
  for (const section of currentFilters) {
    if (!Array.isArray(section.filters)) continue
    for (const filter of section.filters) {
      if (Array.isArray(filter.values) && filter.values.includes(valueKey)) {
        return filter
      }
    }
  }
  return null
}

/** Normalizes a single raw `data` entry into { id, label }. */
const normalizeOption = item => {
  if (Array.isArray(item)) return { id: item[0], label: item[1] }
  if (typeof item === 'string') return { id: item, label: item }
  if (item && typeof item === 'object') {
    const id = item.id ?? item.name ?? item.code
    const label = item.title ?? item.content ?? item.label ?? item.name
    return { id, label: label ?? String(id) }
  }
  return { id: item, label: String(item) }
}

/** Returns the full [{ id, label }] option list for a filter definition. */
const getFilterOptions = filterDef => {
  if (!filterDef || filterDef.data == null) return []
  const { data } = filterDef
  if (Array.isArray(data)) return data.map(normalizeOption)
  if (typeof data === 'object') {
    return Object.entries(data).map(([id, label]) => ({ id, label }))
  }
  return []
}

const rangesEqual = (a, b) => Array.isArray(a) && Array.isArray(b) && a[0] === b[0] && a[1] === b[1]

/** True only when `range` is a 2-item array of actual finite numbers. */
const isValidRange = range =>
  Array.isArray(range) &&
  range.length === 2 &&
  Number.isFinite(range[0]) &&
  Number.isFinite(range[1])

/**
 * True when every id in `fullIds` (the complete set of available options)
 * is present in `selectedIds` (the currently active selection) — i.e. the
 * user hasn't actually narrowed anything down, so the group is effectively
 * "no filter" and shouldn't be displayed as one.
 */
const isEverythingSelected = (selectedIds, fullIds) => {
  if (!Array.isArray(fullIds) || fullIds.length === 0) return false
  if (!Array.isArray(selectedIds) || selectedIds.length === 0) return false
  return fullIds.every(id => selectedIds.includes(id))
}

/**
 * Transforms the raw filter-state object into an ordered list of
 * { title, items } groups matching the app's "Selected ... Filters" sections.
 *
 * @param {object} data - the currently active selections (selectedFilters)
 * @param {Array} currentFilters - the full filter-UI config (sections with
 *   filters/data), used to look up the complete option set and labels for
 *   "everything selected" detection.
 */
function buildFilterGroups(data, currentFilters) {
  if (!data) return []

  const groups = []

  const addGroup = (title, items) => {
    const filtered = items.filter(item => {
      if (item === undefined || item === null || item === '') return false
      const str = String(item)
      // Defensive net: drop anything that leaked an unresolved lookup
      // (e.g. a template string built from a missing id/label).
      if (/\bundefined\b|\bnull\b/.test(str)) return false
      return true
    })
    if (filtered.length) groups.push({ title, items: filtered })
  }

  // 1. Project keywords field filters --------------------------------------
  const keywordItems = []
  if (data.search) {
    // The free-text search terms are always shown, regardless of the
    // "everything selected" state of the fields below.
    ;(data.search.list || []).forEach(term => keywordItems.push(term))

    const selectedFieldKeys = data.search.fields || []
    const fieldsFilterDef = findFilterDef(currentFilters, 'fields')
    const fieldOptions = getFilterOptions(fieldsFilterDef)
    const allFieldIds = fieldOptions.map(o => o.id)
    const fieldLabelById = Object.fromEntries(fieldOptions.map(o => [o.id, o.label]))
    const fieldsFullySelected = isEverythingSelected(selectedFieldKeys, allFieldIds)

    if (!fieldsFullySelected) {
      const seenFieldLabels = new Set()
      selectedFieldKeys.forEach(fieldKey => {
        const label = fieldLabelById[fieldKey]
        if (label && !seenFieldLabels.has(label)) {
          seenFieldLabels.add(label)
          keywordItems.push(label)
        }
      })
    }
  }

  const selectedLangIds = (data.project_lang || []).map(l => l.id)
  const langFilterDef = findFilterDef(currentFilters, 'project_lang')
  const allLangIds = getFilterOptions(langFilterDef).map(o => o.id)
  const langsFullySelected = isEverythingSelected(selectedLangIds, allLangIds)

  if (!langsFullySelected) {
    ;(data.project_lang || []).forEach(l => keywordItems.push(l.label))
  }

  addGroup('Selected project keywords field Filters', keywordItems)

  // 2. Documents filters -----------------------------------------------------
  const documentItems = []
  if (data.documents && data.documents.name) {
    documentItems.push(data.documents.name)
  }
  addGroup('Selected documents Filters', documentItems)

  // 3. Additional document filters -------------------------------------------
  const additionalDocItems = []
  if (data.documents) {
    ;(data.documents.types || []).forEach(t => {
      additionalDocItems.push(DOCUMENT_TYPE_LABELS[t] || t)
    })
    ;(data.documents.document_lang || []).forEach(l => additionalDocItems.push(l.label))
  }
  ;(data.project_desc_lang || []).forEach(l => additionalDocItems.push(l.label))
  addGroup('Selected additional document Filters', additionalDocItems)

  // 4. Funding filters (nuts regions + programme type/period/available) ------
  const fundingItems = []
  if (data.partners) {
    ;(data.partners.nuts_lead || []).forEach(n => fundingItems.push(`Lead partner ${n.label}`))
    ;(data.partners.nuts_partner || []).forEach(n =>
      fundingItems.push(`Project partner ${n.label}`)
    )
  }
  if (data.programmes) {
    ;(data.programmes.period || []).forEach(p => fundingItems.push(p.label))
    ;(data.programmes.type || []).forEach(t => fundingItems.push(t.label))
    ;(data.programmes.available || []).forEach(a => fundingItems.push(a.label))
  }
  addGroup('Selected funding Filters', fundingItems)

  // 5. Thematic focus filters -------------------------------------------------
  const thematicItems = []
  if (data.projects && data.projects.themes) {
    ;(data.projects.themes.list || []).forEach(t => thematicItems.push(t.label))
  }
  addGroup('Selected thematic focus Filters', thematicItems)

  // 6. Times and budget filters ------------------------------------------------
  const timesBudgetItems = []
  if (data.projects) {
    if (data.projects.status === 'closed') timesBudgetItems.push('Only closed projects')
    if (data.projects.status === 'ongoing') timesBudgetItems.push('Only ongoing projects')

    const pd = data.projects.project_details
    if (pd) {
      if (
        !pd.without_start &&
        isValidRange(pd.start) &&
        !rangesEqual(pd.start, DEFAULT_YEAR_RANGE)
      ) {
        timesBudgetItems.push(`Start year from: ${pd.start[0]} to: ${pd.start[1]}`)
      }
      if (!pd.without_end && isValidRange(pd.end) && !rangesEqual(pd.end, DEFAULT_YEAR_RANGE)) {
        timesBudgetItems.push(`End year from: ${pd.end[0]} to: ${pd.end[1]}`)
      }
    }

    const budget = data.projects.project_budget
    if (budget && !budget.without_budget && isValidRange(budget.range)) {
      timesBudgetItems.push(`Budget from: ${budget.range[0]} to: ${budget.range[1]}`)
    }
  }
  addGroup('Selected times and budget Filters', timesBudgetItems)

  // 7. Partners filters ---------------------------------------------------------
  const partnersItems = []
  if (data.partners) {
    ;(data.partners.status || []).forEach(s => partnersItems.push(s))
    ;(data.partners.type || []).forEach(t => partnersItems.push(t))
  }
  addGroup('Selected partners Filters', partnersItems)

  // 8. 2021-2027 contribution filters --------------------------------------------
  const c2127Items = []
  if (data.contribution_2021_2027) {
    ;(data.contribution_2021_2027.policy_objective_interreg_specific_objective || []).forEach(p =>
      c2127Items.push(p.label)
    )
    ;(data.contribution_2021_2027.specific_objectives || []).forEach(s => c2127Items.push(s.label))
    ;(data.contribution_2021_2027.intervention || []).forEach(i => c2127Items.push(i.label))
  }
  addGroup('Selected 2021-2027 Filters', c2127Items)

  // 9. 2014-2020 contribution filters ----------------------------------------------
  const c1420Items = []
  if (data.contribution_2014_2020 && data.contribution_2014_2020.specific_objectives) {
    const so = data.contribution_2014_2020.specific_objectives
    ;(so.thematic_objectives || []).forEach(to => {
      ;(to.investment_priorities || []).forEach(ip => c1420Items.push(ip.label))
    })
  }
  if (data.contribution_2014_2020) {
    ;(data.contribution_2014_2020.thematic_objectives_eni || []).forEach(t =>
      c1420Items.push(t.label)
    )
    if (data.contribution_2014_2020.specific_objectives) {
      ;(data.contribution_2014_2020.specific_objectives.thematic_priorities || []).forEach(tp =>
        c1420Items.push(tp.label)
      )
    }
  }
  addGroup('Selected 2014-2020 Filters', c1420Items)

  // 10. Additional data filters --------------------------------------------------------
  const additionalDataItems = []
  if (data.projects) {
    if (data.projects.only_infrastructure_financed) {
      additionalDataItems.push('Show only projects with investments in infrastructure')
    }
    if (data.projects.only_projects_with_documents) {
      additionalDataItems.push('Show only projects with documents')
    }
    if (data.projects.prizes) {
      additionalDataItems.push('Show only projects that have won prizes')
    }
    ;(data.projects.macro_regional_strategies || []).forEach(code => {
      additionalDataItems.push(MACRO_REGIONAL_STRATEGY_LABELS[code] || String(code).toUpperCase())
    })
  }
  addGroup('Selected additional data Filters', additionalDataItems)

  return groups
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const FilterSelection = ({ selectedFilters, currentFilters, onFilterCountChange }) => {
  const groups = useMemo(
    () => buildFilterGroups(selectedFilters, currentFilters),
    [selectedFilters, currentFilters]
  )

  const totalCount = useMemo(() => groups.reduce((sum, g) => sum + g.items.length, 0), [groups])

  useEffect(() => {
    onFilterCountChange?.(totalCount)
  }, [totalCount, onFilterCountChange])

  if (!groups.length) {
    return null
  }

  return (
    <div
      style={{
        padding: '1rem',
        borderRadius: '4px',
        overflowX: 'auto'
      }}
    >
      <h4 className="moderate">Search criteria</h4>
      <div className="results-tip">
        {groups.map(({ title, items }, groupIndex) => (
          <React.Fragment key={title}>
            {items.map((item, index) => (
              <div
                className="tipped"
                key={`${groupIndex}-${index}`}
                title={`${title}: ${item}`}
              >
                {item.length > 40 ? `${item.slice(0, 40)}...` : item}
                {index === items.length - 1 ? "" : ", "}
              </div>
            ))}

            {groupIndex < groups.length - 1 && (
              <div className="pipe"></div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

FilterSelection.propTypes = {
  selectedFilters: PropTypes.object,
  currentFilters: PropTypes.array,
  onFilterCountChange: PropTypes.func
}

export default FilterSelection
